import React, { useState, useEffect, useRef } from 'react';
import { Button, Icon, Intent, Collapse, Tag } from '@blueprintjs/core';
import { observer } from 'mobx-react-lite';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import './BackendLogsViewer.css';

export interface LogEntry {
  id: string;
  timestamp: Date;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  status?: number;
  duration?: number;
  request?: any;
  response?: any;
  error?: any;
}

interface BackendLogsViewerProps {
  maxLogs?: number;
}

// Singleton to collect logs from anywhere in the app
class LogCollector {
  private static instance: LogCollector;
  private listeners: ((log: LogEntry) => void)[] = [];
  private logs: LogEntry[] = [];
  private maxLogs = 100;

  static getInstance(): LogCollector {
    if (!LogCollector.instance) {
      LogCollector.instance = new LogCollector();
    }
    return LogCollector.instance;
  }

  addLog(log: LogEntry) {
    this.logs.push(log);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    this.listeners.forEach(listener => listener(log));
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  subscribe(listener: (log: LogEntry) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  clear() {
    this.logs = [];
    this.listeners.forEach(listener => listener({ 
      id: 'clear', 
      timestamp: new Date(), 
      method: 'GET', 
      url: '' 
    }));
  }
}

export const logCollector = LogCollector.getInstance();

const BackendLogsViewer: React.FC<BackendLogsViewerProps> = observer(({ maxLogs = 50 }) => {
  const appStore = useAppStore();
  const { auth } = appStore;
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const logsEndRef = useRef<HTMLDivElement>(null);

  // Only show for admin or owner roles
  const isAdmin = auth.user?.role === 'admin' || auth.user?.role === 'owner';

  if (!isAdmin) {
    return null;
  }

  useEffect(() => {
    setLogs(logCollector.getLogs());
    
    const unsubscribe = logCollector.subscribe((log) => {
      if (log.id === 'clear') {
        setLogs([]);
      } else {
        setLogs(prev => {
          const newLogs = [...prev, log];
          if (newLogs.length > maxLogs) {
            newLogs.shift();
          }
          return newLogs;
        });
      }
    });

    return unsubscribe;
  }, [maxLogs]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, isOpen, isMinimized]);

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const getMethodColor = (method: string): Intent => {
    switch (method) {
      case 'GET': return Intent.PRIMARY;
      case 'POST': return Intent.SUCCESS;
      case 'PUT': return Intent.WARNING;
      case 'PATCH': return Intent.WARNING;
      case 'DELETE': return Intent.DANGER;
      default: return Intent.NONE;
    }
  };

  const getStatusColor = (status?: number): Intent => {
    if (!status) return Intent.NONE;
    if (status >= 200 && status < 300) return Intent.SUCCESS;
    if (status >= 300 && status < 400) return Intent.WARNING;
    if (status >= 400 && status < 500) return Intent.DANGER;
    if (status >= 500) return Intent.DANGER;
    return Intent.NONE;
  };

  const formatDuration = (duration?: number): string => {
    if (!duration) return '';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  if (!isOpen) {
    return (
      <Button
        className="backend-logs-fab"
        icon="console"
        intent={Intent.PRIMARY}
        onClick={() => setIsOpen(true)}
        title="Open Backend Logs"
      />
    );
  }

  return (
    <div className={`backend-logs-viewer ${isMinimized ? 'minimized' : ''}`}>
      <div className="logs-header">
        <div className="logs-header-title">
          <Icon icon="console" />
          <span>Backend Logs</span>
          <Tag minimal>{logs.length}</Tag>
        </div>
        <div className="logs-header-actions">
          <Button
            icon="trash"
            minimal
            small
            onClick={() => logCollector.clear()}
            title="Clear logs"
          />
          <Button
            icon={isMinimized ? "maximize" : "minimize"}
            minimal
            small
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? "Expand" : "Minimize"}
          />
          <Button
            icon="cross"
            minimal
            small
            onClick={() => setIsOpen(false)}
            title="Close"
          />
        </div>
      </div>
      
      <Collapse isOpen={!isMinimized}>
        <div className="logs-content">
          {logs.length === 0 ? (
            <div className="logs-empty">No logs yet...</div>
          ) : (
            logs.map(log => (
              <div key={log.id} className={`log-entry ${log.error ? 'error' : ''}`}>
                <div 
                  className="log-summary"
                  onClick={() => toggleLogExpansion(log.id)}
                >
                  <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
                  <Tag 
                    intent={getMethodColor(log.method)}
                    minimal
                    className="log-method"
                  >
                    {log.method}
                  </Tag>
                  <span className="log-url">{log.url}</span>
                  {log.status && (
                    <Tag 
                      intent={getStatusColor(log.status)}
                      minimal
                      className="log-status"
                    >
                      {log.status}
                    </Tag>
                  )}
                  {log.duration && (
                    <span className="log-duration">{formatDuration(log.duration)}</span>
                  )}
                  <Icon 
                    icon={expandedLogs.has(log.id) ? "chevron-up" : "chevron-down"}
                    className="log-expand-icon"
                  />
                </div>
                
                <Collapse isOpen={expandedLogs.has(log.id)}>
                  <div className="log-details">
                    {log.request && (
                      <div className="log-section">
                        <div className="log-section-title">Request:</div>
                        <pre className="log-json">
                          {JSON.stringify(log.request, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.response && (
                      <div className="log-section">
                        <div className="log-section-title">Response:</div>
                        <pre className="log-json">
                          {JSON.stringify(log.response, null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.error && (
                      <div className="log-section">
                        <div className="log-section-title">Error:</div>
                        <pre className="log-json error">
                          {typeof log.error === 'string' ? log.error : JSON.stringify(log.error, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </Collapse>
    </div>
  );
});

export default BackendLogsViewer;
